import React, { FC, useCallback, useMemo } from "react";

import Dropdown from "antd/es/dropdown";
import { MenuProps } from "antd/es/menu";
import { MenuInfo } from "rc-menu/lib/interface";
import { defineMessages, useIntl } from "react-intl";

import RoundButton from "@app/component/RoundButton";
import { DateFilterValue, DateUnit } from "@app/scene/onboarding/types/DateUnit";
import useAnalytics from "@app/util/contexts/useAnalytics";

import styles from "./CommonFilter.module.scss";

const CLEAR_OPTION_VALUE = "clear";
const OPTIONS: DateFilterValue[] = [
  { amount: 15, unit: "days" },
  { amount: 3, unit: "months" },
  { amount: 6, unit: "months" },
  { amount: 1, unit: "years" },
];

const messages = defineMessages<DateUnit | typeof CLEAR_OPTION_VALUE>({
  [CLEAR_OPTION_VALUE]: {
    id: "onboarding.users.filters.creation.options.clear",
    defaultMessage: "Clear",
    description: "Clear option in Creation filter on the Users Guide page",
  },
  days: {
    id: "onboarding.users.filters.creation.options.days",
    defaultMessage: "{count} {count, plural, one {day} other {days}}",
    description: "Day option in Creation filter on the Users Guide page",
  },
  months: {
    id: "onboarding.users.filters.creation.options.months",
    defaultMessage: "{count} {count, plural, one {month} other {months}}",
    description: "Month option in Creation filter on the Users Guide page",
  },
  years: {
    id: "onboarding.users.filters.creation.options.years",
    defaultMessage: "{count} {count, plural, one {year} other {years}}",
    description: "Year option in Creation filter on the Users Guide page",
  },
});

export interface CompletionFilterProps {
  onChange: (value?: DateFilterValue) => void;
  value?: DateFilterValue;
}

const CompletionFilter: FC<CompletionFilterProps> = ({ onChange, value }) => {
  const intl = useIntl();
  const analytics = useAnalytics();

  const handleMenuClick = useCallback(
    (info: MenuInfo) => {
      analytics.using(["Creation Filter"]);
      const value = info.key as number | typeof CLEAR_OPTION_VALUE;
      if (value === CLEAR_OPTION_VALUE) {
        onChange(undefined);
      } else {
        onChange(OPTIONS[value as number]);
      }
    },
    [analytics, onChange]
  );

  const menuProps = useMemo(() => {
    const items: MenuProps["items"] = [
      {
        className: styles.clearMenuItem,
        key: CLEAR_OPTION_VALUE,
        label: intl.formatMessage(messages[CLEAR_OPTION_VALUE]),
      },
      ...OPTIONS.map((option, index) => ({
        key: index,
        label: intl.formatMessage(messages[option.unit], { count: option.amount }),
      })),
    ];
    return {
      activeKey: value ? String(OPTIONS.indexOf(value)) : undefined,
      items,
      onClick: handleMenuClick,
    };
  }, [handleMenuClick, intl, value]);

  return (
    <Dropdown menu={menuProps} overlayClassName={styles.dropdownOverlay} trigger={["click"]}>
      <RoundButton active={!!value} hasMenu>
        {intl.formatMessage(
          {
            id: "onboarding.users.filters.creation.title",
            defaultMessage: "Joined in the last{hasValue, select, true {: {value}} other {}}",
            description: "Title of the Creation filter button on the User Guides page",
          },
          {
            hasValue: !!value,
            value: value
              ? intl.formatMessage(messages[value.unit], { count: value.amount })
              : undefined,
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

export default CompletionFilter;
